import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Info } from "lucide-react";

const VIEWPORT_MARGIN = 12;
const MAX_WIDTH = 280;
const GAP = 6;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  background: none;
  color: #b0a599;
  cursor: help;
  border-radius: 50%;
  flex-shrink: 0;

  &:hover,
  &:focus-visible {
    color: #4a423b;
    outline: none;
  }
`;

const Bubble = styled.div`
  position: fixed;
  z-index: 2000;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 10px;
  background: #1f1b17;
  color: #f4efe8;
  font-family: 'Aileron', 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  text-transform: none;
  letter-spacing: normal;
  text-align: left;
  white-space: pre-line;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  pointer-events: none;
`;

interface InfoTipProps {
  text: string;
  label?: string;
}

// Rendered into document.body with fixed positioning, then clamped to the
// viewport, so it can't run off-screen or be clipped by a scrolling container.
const InfoTip = ({ text, label = "More information" }: InfoTipProps) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const id = `info-${useId().replace(/:/g, "")}`;

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    const width = Math.min(MAX_WIDTH, vw - VIEWPORT_MARGIN * 2);
    const left = Math.min(
      Math.max(rect.left + rect.width / 2 - width / 2, VIEWPORT_MARGIN),
      vw - width - VIEWPORT_MARGIN
    );
    const height = bubbleRef.current?.offsetHeight ?? 0;
    const below = rect.bottom + GAP;
    const top =
      below + height > vh - VIEWPORT_MARGIN && rect.top - GAP - height > VIEWPORT_MARGIN
        ? rect.top - GAP - height
        : below;
    setPos({ top, left, width });
  }, []);

  // The bubble is already in the DOM when this runs, so its height is known.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <>
      <Trigger
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-describedby={open ? id : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((o) => !o)}
      >
        <Info size={14} />
      </Trigger>
      {open &&
        createPortal(
          <Bubble
            ref={bubbleRef}
            id={id}
            role="tooltip"
            style={{
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              width: pos?.width ?? MAX_WIDTH,
            }}
          >
            {text}
          </Bubble>,
          document.body
        )}
    </>
  );
};

export default InfoTip;

export const EARNINGS_NOTE =
  "Calculated the same way as your payouts: your share of the full plan price for every membership checked in during the month (UTC). Each membership counts in the month of its first check-in, so a month's total is final once the month ends.";

export const AWAITING_CHECK_IN_NOTE =
  "Current memberships that haven't been checked in yet. Each one is added to your earnings, and paid out, in the month the member checks in.";

export const PROJECTION_NOTE =
  "Your share from your current members who are set to upgrade, or renew on your upgrade plan, in the next 31 days. Excludes anyone cancelling or paused and any subscription that isn't active in our billing system. Like all earnings, each one is paid out once the member checks in.";
